import { Grid, Skeleton, Typography } from "@mui/material";
import { observer } from "mobx-react";
import React, { useMemo } from "react";
import Classifier from "../../../../../../../models/ClassifierModel";
import { ClassifierResultAnswerTag } from "../../../../../../../models/ClassifierResultAnswerTag";
import ClassifierService from "../../../../../../../services/ClassifierService";
import AcxSelect from "../../../../../../UI/Select/BaseSelectComponents/AcxSelect";
import { Question, Tag } from "../../../../types/Module.type";
import theme from "Theme/AppTheme";
import { Warning } from "@mui/icons-material";

type TagToClassifier = { [tagId: string]: Classifier };
type TagToResult = { [tagId: string]: ClassifierResultAnswerTag };

export interface SmartPredictConfigHandle {
    getMappings: () => ClassifierResultAnswerTag[];
}

interface LabelSmartPredictConfigProps {
    orgId: string;
    classifiers: Classifier[] | undefined;
    question?: Question;
    tags: Tag[];
    // Reports why the config is invalid (used as the Apply tooltip), or null when valid.
    onValidityChange?: (invalidReason: string | null) => void;
    // Reports once the user has edited any mapping since load (one-way; never resets on revert).
    onDirtyChange?: (dirty: boolean) => void;
}

export const LabelSmartPredictConfig = observer(
    React.forwardRef<SmartPredictConfigHandle, LabelSmartPredictConfigProps>(
        (props, ref) => {
        // Below are two state objects (mapping tagId to Classifier and tagId to ClassifierResultAnswerTag)
        const [tagToClassifierMap, setTagToClassifiertMap] =
            React.useState<TagToClassifier>({});

        const [tagToResultMap, setTagToResultMap] = React.useState<TagToResult>(
            {},
        );

        const [loading, setLoading] = React.useState(true);
        const [dirty, setDirty] = React.useState(false);
        const markDirty = React.useCallback(() => setDirty(true), []);

        // SmartPredict mappings only apply to active tags that have been persisted. Inactive tags
        // are excluded, and newly-added (unsaved) tags have no id yet — configuring them would save
        // mappings against an id the backend never assigns, so they're hidden until the module saves.
        const activeTags = useMemo(
            () => props.tags.filter((tag) => tag.isActive && !!tag.id),
            [props.tags],
        );

        const hasUnsavedActiveTags = useMemo(
            () => props.tags.some((tag) => tag.isActive && !tag.id),
            [props.tags],
        );

        const hasLLMAndOtherClassifierTypeSelected = useMemo(() => {
            const classifiers = Object.values(tagToClassifierMap);
            return (
                classifiers.some((c) => c.classifierTypeName === "LLM") &&
                classifiers.some((c) => c.classifierTypeName !== "LLM")
            );
        }, [tagToClassifierMap]);

        React.useImperativeHandle(
            ref,
            () => {
                const activeTagIds = new Set(activeTags.map((tag) => tag.id));
                return {
                    getMappings: () =>
                        Object.values(tagToResultMap).filter((result) =>
                            activeTagIds.has(result.tagId),
                        ),
                };
            },
            [tagToResultMap, activeTags],
        );

        const { onValidityChange } = props;
        React.useEffect(() => {
            // Tags may be left without a classifier result; the only hard requirement is that
            // selected classifiers don't mix LLM with non-LLM types.
            const invalidReason = hasLLMAndOtherClassifierTypeSelected
                ? "Selected classifiers must be the same type"
                : null;
            onValidityChange?.(invalidReason);
        }, [hasLLMAndOtherClassifierTypeSelected, onValidityChange]);

        const { onDirtyChange } = props;
        React.useEffect(() => {
            onDirtyChange?.(dirty);
        }, [dirty, onDirtyChange]);

        const removeSelectedResult = (tagId: string) => {
            let tagToResultMapClone = { ...tagToResultMap };
            delete tagToResultMapClone[tagId];
            setTagToResultMap(tagToResultMapClone);
        };

        const handleClassifierChange = (event, tag: Tag) => {
            const selectedClassifier = props.classifiers?.find(
                (classifier) => classifier.id === event.id,
            );

            const changed =
                tagToClassifierMap[tag.id] === undefined ||
                tagToClassifierMap[tag.id] !== selectedClassifier;

            if (changed) {
                markDirty();
                setTagToClassifiertMap(
                    (prevState) =>
                        ({
                            ...prevState,
                            [tag.id]: selectedClassifier,
                        } as TagToClassifier),
                );
                // If we've changed the classifier, we need to remove the tagId to ClassifierResultAnswerTag
                // so the user has to select a new Result. This is because different classifiers can have different ClassifierOptions (Results)
                removeSelectedResult(tag.id);
            }
        };

        const handleClassifierResultChange = (event, tag: Tag) => {
            markDirty();
            // If "No Selection" is selected, remove that ClassifierResult
            if (event === undefined) {
                removeSelectedResult(tag.id);
                return;
            }

            setTagToResultMap(
                (prevState) =>
                    ({
                        ...prevState,
                        [tag.id]: {
                            questionId: props.question?.id,
                            organizationId: props.orgId,
                            classifierId: tagToClassifierMap[tag.id].id,
                            tagId: tag.id,
                            result: event.result,
                            classifierOutputId: event.id,
                        } as ClassifierResultAnswerTag,
                    } as TagToResult),
            );
        };

        React.useEffect(() => {
            let cancelled = false;
            const getExistingConfig = async () => {
                if (!(props.question && props.question.id && props.orgId)) {
                    if (!cancelled) setLoading(false);
                    return;
                }

                setLoading(true);
                try {
                    const existingAutoScoreConfig: ClassifierResultAnswerTag[] =
                        await new ClassifierService().getAutoScoringConfiguration(
                            props.orgId,
                            props.question.id,
                        );
                    if (cancelled) return;

                    // Set TagId to Classifier and Set TagId to Result
                    let tagsToClassifier: TagToClassifier = {};

                    let tagsToResult: TagToResult = {};

                    existingAutoScoreConfig.forEach(
                        (classifierResultAnswerTag) => {
                            tagsToClassifier[classifierResultAnswerTag.tagId] =
                                classifierResultAnswerTag.classifier;

                            tagsToResult[classifierResultAnswerTag.tagId] =
                                classifierResultAnswerTag;
                        },
                    );

                    setTagToClassifiertMap(tagsToClassifier);
                    setTagToResultMap(tagsToResult);
                } finally {
                    if (!cancelled) setLoading(false);
                }
            };

            getExistingConfig();
            return () => {
                cancelled = true;
            };
        }, [props.orgId, props.question]);

        return (
            <Grid
                container
                justifyContent="center"
                alignContent="center"
                style={{ margin: "1rem 0" }}
            >
                {hasUnsavedActiveTags && (
                    <Grid
                        item
                        xs={12}
                        sx={{
                            flexBasis: "100%",
                            maxWidth: "100%",
                            minWidth: 0,
                            marginBottom: "0.5rem",
                        }}
                    >
                        <Typography
                            color="text.secondary"
                            variant="body2"
                            sx={{
                                width: "100%",
                                whiteSpace: "normal",
                                overflowWrap: "anywhere",
                            }}
                        >
                            Save the module to configure auto-scoring for newly
                            added tags.
                        </Typography>
                    </Grid>
                )}
                {loading ? (
                    (activeTags.length ? activeTags : [...Array(2)]).map(
                        (tag, index) => (
                            <Grid
                                key={tag?.id ?? index}
                                container
                                style={{
                                    margin: "0.25rem",
                                    backgroundColor: theme.palette.lightgray.main,
                                    padding: theme.spacing(1),
                                    borderRadius: theme.shape.borderRadius,
                                }}
                            >
                                <Skeleton width="40%" height={24} />
                                <Skeleton
                                    variant="rectangular"
                                    width="100%"
                                    height={40}
                                    sx={{ borderRadius: 1, marginBottom: 1 }}
                                />
                                <Skeleton
                                    variant="rectangular"
                                    width="100%"
                                    height={40}
                                    sx={{ borderRadius: 1 }}
                                />
                            </Grid>
                        ),
                    )
                ) : props.tags ? (
                    activeTags
                        .sort((tag1, tag2) => tag1.order - tag2.order)
                        .map((tag) => {
                            return (
                                <Grid
                                    key={tag.id}
                                    container
                                    justifyContent="space-between"
                                    alignItems="center"
                                    style={{
                                        margin: "0.25rem",
                                        backgroundColor:
                                            theme.palette.lightgray.main,
                                        padding: theme.spacing(1),
                                        borderRadius: theme.shape.borderRadius,
                                    }}
                                >
                                    <Grid
                                        container
                                        item
                                        xs={12}
                                        style={{ marginBottom: "5px" }}
                                    >
                                        <Typography
                                            sx={{
                                                color: "#374151",
                                                fontFamily: "Inter",
                                                fontSize: "14px",
                                                fontStyle: "normal",
                                                fontWeight: 600,
                                                lineHeight: "20px",
                                            }}
                                        >
                                            Tag: {tag.value}
                                        </Typography>
                                    </Grid>

                                    <Grid
                                        container
                                        item
                                        xs={12}
                                        style={{ marginBottom: "5px" }}
                                    >
                                        <Typography
                                            style={{
                                                ...theme.typography.h6,
                                                opacity: 1,
                                            }}
                                        >
                                            SmartPredict Classifier
                                        </Typography>
                                        <AcxSelect
                                            fullWidth={false}
                                            defaultValue={
                                                tagToClassifierMap[tag.id]
                                            }
                                            id={`auto-scoring-classifier-selector-${props.question?.id}`}
                                            labelField="name"
                                            valueField="id"
                                            options={props.classifiers ?? []}
                                            onChange={(event) =>
                                                handleClassifierChange(
                                                    event,
                                                    tag,
                                                )
                                            }
                                            placeholder={"Classifier"}
                                        />
                                    </Grid>

                                    <Grid container item xs={12}>
                                        <Typography
                                            style={{
                                                ...theme.typography.h6,
                                                opacity: 1,
                                            }}
                                        >
                                            Result
                                        </Typography>
                                        <AcxSelect
                                            enableNoSelection
                                            fullWidth={false}
                                            isDisabled={
                                                tagToClassifierMap[tag.id] ===
                                                undefined
                                            }
                                            defaultValue={
                                                tagToResultMap[tag.id]
                                            }
                                            id={`auto-scoring-classifier-result-selector-${props.question?.id}`}
                                            labelField="result"
                                            valueField="id"
                                            options={
                                                tagToClassifierMap[tag.id]
                                                    ?.classifierOutputs ?? []
                                            }
                                            onChange={(event) =>
                                                handleClassifierResultChange(
                                                    event,
                                                    tag,
                                                )
                                            }
                                            placeholder={"Classifier Result"}
                                        />
                                    </Grid>
                                </Grid>
                            );
                        })
                ) : null}

                <Grid
                    container
                    item
                    direction="column"
                    rowGap={1}
                    sx={{
                        margin: "0.25rem",
                    }}
                >
                    {hasLLMAndOtherClassifierTypeSelected && (
                        <Grid
                            container
                            item
                            alignItems="center"
                            columnGap={1}
                            flexWrap="nowrap"
                            sx={(theme) => ({
                                color: theme.palette.error.main,
                                backgroundColor: "#FEE2E2",
                                padding: theme.spacing(0.5),
                                borderRadius: 1,
                            })}
                        >
                            <Warning />
                            <Grid item>
                                <Typography
                                    sx={{
                                        color: "inherit",
                                        whiteSpace: "normal",
                                    }}
                                >
                                    Please select classifiers of the same type.
                                    LLM-based classifiers cannot be used with
                                    other types of classifiers.
                                </Typography>
                            </Grid>
                        </Grid>
                    )}
                </Grid>
            </Grid>
        );
        },
    ),
);

LabelSmartPredictConfig.displayName = "LabelSmartPredictConfig";
